/* ===========================================================
 * useDockLayout
 * -----------------------------------------------------------
 * Verwaltet das Layout-State für die Composer-Panels:
 *  - Pro Panel ein Modus: "docked" | "collapsed" | "floating" | "hidden"
 *  - Pro Panel eine Float-Geometrie (x, y, w, h)
 *  - Spaltenbreiten (links / rechts) für die Resize-Handles
 *  - Persistenz in localStorage, automatisch geladen beim Start.
 *
 * Außerdem: Auto-Adapt an Viewport-Größen.
 *  - Mobile  (< 760px):  alle Side-Panels gehen als Drawer
 *  - Tablet  (760-1199): rechte Spalte schmaler / kollapsbar
 *  - Desktop (>= 1200):  drei Spalten, frei resizbar
 * =========================================================== */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type PanelId = "library" | "inspector" | "timeline";
export type PanelMode = "docked" | "collapsed" | "floating" | "hidden";

export type FloatRect = { x: number; y: number; w: number; h: number };

export type DockLayoutState = {
  modes: Record<PanelId, PanelMode>;
  floats: Record<PanelId, FloatRect>;
  /** Spaltenbreiten in Pixel — werden per CSS-Variablen ausgespielt. */
  colLeft: number;
  colRight: number;
  /** Höhe der Timeline-Sektion (im Stage). */
  timelineH: number;
};

const STORAGE_KEY = "jusa.composer.dock.v1";

const DEFAULT_FLOAT: Record<PanelId, FloatRect> = {
  library: { x: 24, y: 96, w: 360, h: 560 },
  inspector: { x: window.innerWidth - 384, y: 96, w: 360, h: 600 },
  timeline: { x: 24, y: window.innerHeight - 280, w: 900, h: 240 },
};

const DEFAULT_STATE: DockLayoutState = {
  modes: { library: "docked", inspector: "docked", timeline: "docked" },
  floats: DEFAULT_FLOAT,
  colLeft: 320,
  colRight: 340,
  timelineH: 220,
};

const MIN_COL = 220;
const MAX_COL = 520;
const MIN_FLOAT_W = 280;
const MIN_FLOAT_H = 180;

function loadFromStorage(): Partial<DockLayoutState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<DockLayoutState>;
    return parsed;
  } catch {
    return {};
  }
}

function pickMode(persisted: PanelMode | undefined, fallback: PanelMode): PanelMode {
  return persisted === "docked" || persisted === "collapsed" || persisted === "floating" || persisted === "hidden"
    ? persisted
    : fallback;
}

export function useDockLayout(): {
  state: DockLayoutState;
  /** Aktueller Viewport-Tier — Komponenten dürfen layout-bedingte Entscheidungen treffen. */
  viewport: "mobile" | "tablet" | "desktop";
  setMode: (id: PanelId, mode: PanelMode) => void;
  cycleCollapse: (id: PanelId) => void;
  togglePopup: (id: PanelId) => void;
  setFloat: (id: PanelId, rect: Partial<FloatRect>) => void;
  setColumn: (which: "left" | "right", px: number) => void;
  setTimelineH: (px: number) => void;
  resetLayout: () => void;
} {
  const [state, setState] = useState<DockLayoutState>(() => {
    const persisted = loadFromStorage();
    return {
      ...DEFAULT_STATE,
      ...persisted,
      modes: {
        library: pickMode(persisted.modes?.library, DEFAULT_STATE.modes.library),
        inspector: pickMode(persisted.modes?.inspector, DEFAULT_STATE.modes.inspector),
        timeline: pickMode(persisted.modes?.timeline, DEFAULT_STATE.modes.timeline),
      },
      floats: {
        library: { ...DEFAULT_STATE.floats.library, ...(persisted.floats?.library ?? {}) },
        inspector: { ...DEFAULT_STATE.floats.inspector, ...(persisted.floats?.inspector ?? {}) },
        timeline: { ...DEFAULT_STATE.floats.timeline, ...(persisted.floats?.timeline ?? {}) },
      },
      colLeft: clamp(persisted.colLeft ?? DEFAULT_STATE.colLeft, MIN_COL, MAX_COL),
      colRight: clamp(persisted.colRight ?? DEFAULT_STATE.colRight, MIN_COL, MAX_COL),
      timelineH: clamp(persisted.timelineH ?? DEFAULT_STATE.timelineH, 120, 600),
    };
  });

  /* --- Viewport tier (rAF-throttled resize listener) --- */
  const [viewport, setViewport] = useState<"mobile" | "tablet" | "desktop">(() => detectViewport());
  useEffect(() => {
    let frame = 0;
    const onResize = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setViewport(detectViewport());
        // Floats nach Viewport-Resize wieder in den sichtbaren Bereich klemmen,
        // damit Popup-Panels nicht außerhalb des Browsers landen oder die Stage
        // überlappen, wenn z.B. die Sidebar aufgeklappt wird.
        setState((s) => {
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          let changed = false;
          const next: Record<PanelId, FloatRect> = { ...s.floats };
          (Object.keys(s.floats) as PanelId[]).forEach((id) => {
            const f = s.floats[id];
            const w = clamp(f.w, MIN_FLOAT_W, Math.max(MIN_FLOAT_W, vw - 24));
            const h = clamp(f.h, MIN_FLOAT_H, Math.max(MIN_FLOAT_H, vh - 80));
            const x = clamp(f.x, 12, Math.max(12, vw - w - 12));
            const y = clamp(f.y, 60, Math.max(60, vh - h - 12));
            if (x !== f.x || y !== f.y || w !== f.w || h !== f.h) {
              next[id] = { x, y, w, h };
              changed = true;
            }
          });
          return changed ? { ...s, floats: next } : s;
        });
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  /* --- Persistenz (debounced) --- */
  const persistTimer = useRef<number | null>(null);
  useEffect(() => {
    if (persistTimer.current !== null) window.clearTimeout(persistTimer.current);
    persistTimer.current = window.setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        /* Storage voll oder verweigert — Layout funktioniert trotzdem. */
      }
    }, 220);
    return () => {
      if (persistTimer.current !== null) window.clearTimeout(persistTimer.current);
    };
  }, [state]);

  const setMode = useCallback((id: PanelId, mode: PanelMode): void => {
    setState((s) => ({ ...s, modes: { ...s.modes, [id]: mode } }));
  }, []);

  const cycleCollapse = useCallback((id: PanelId): void => {
    setState((s) => {
      const cur = s.modes[id];
      const next: PanelMode = cur === "collapsed" ? "docked" : "collapsed";
      return { ...s, modes: { ...s.modes, [id]: next } };
    });
  }, []);

  const togglePopup = useCallback((id: PanelId): void => {
    setState((s) => {
      const cur = s.modes[id];
      const next: PanelMode = cur === "floating" ? "docked" : "floating";
      // Bei erstem Floaten: Panel an die "richtige" Bildschirmkante andocken,
      // damit es nicht mitten in der Stage landet und Bedienelemente überdeckt:
      //   - library  → linke Kante
      //   - inspector → rechte Kante
      //   - timeline  → unten
      const f = s.floats[id];
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = clamp(f.w, MIN_FLOAT_W, Math.max(MIN_FLOAT_W, vw - 24));
      const h = clamp(f.h, MIN_FLOAT_H, Math.max(MIN_FLOAT_H, vh - 80));
      let x = f.x;
      let y = f.y;
      if (next === "floating") {
        if (id === "library") {
          x = 16;
          y = 76;
        } else if (id === "inspector") {
          x = Math.max(16, vw - w - 16);
          y = 76;
        } else if (id === "timeline") {
          x = 16;
          y = Math.max(60, vh - h - 16);
        }
      }
      x = clamp(x, 12, Math.max(12, vw - w - 12));
      y = clamp(y, 60, Math.max(60, vh - h - 12));
      const rect: FloatRect = next === "floating" ? { x, y, w, h } : f;
      return {
        ...s,
        modes: { ...s.modes, [id]: next },
        floats: { ...s.floats, [id]: rect },
      };
    });
  }, []);

  const setFloat = useCallback((id: PanelId, rect: Partial<FloatRect>): void => {
    setState((s) => {
      const cur = s.floats[id];
      const merged: FloatRect = {
        x: clamp(rect.x ?? cur.x, 0, Math.max(0, window.innerWidth - 80)),
        y: clamp(rect.y ?? cur.y, 0, Math.max(0, window.innerHeight - 60)),
        w: clamp(rect.w ?? cur.w, MIN_FLOAT_W, window.innerWidth),
        h: clamp(rect.h ?? cur.h, MIN_FLOAT_H, window.innerHeight),
      };
      return { ...s, floats: { ...s.floats, [id]: merged } };
    });
  }, []);

  const setColumn = useCallback((which: "left" | "right", px: number): void => {
    setState((s) => ({
      ...s,
      colLeft: which === "left" ? clamp(px, MIN_COL, MAX_COL) : s.colLeft,
      colRight: which === "right" ? clamp(px, MIN_COL, MAX_COL) : s.colRight,
    }));
  }, []);

  const setTimelineH = useCallback((px: number): void => {
    setState((s) => ({ ...s, timelineH: clamp(px, 120, 600) }));
  }, []);

  const resetLayout = useCallback((): void => {
    setState(DEFAULT_STATE);
  }, []);

  return useMemo(
    () => ({ state, viewport, setMode, cycleCollapse, togglePopup, setFloat, setColumn, setTimelineH, resetLayout }),
    [state, viewport, setMode, cycleCollapse, togglePopup, setFloat, setColumn, setTimelineH, resetLayout],
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function detectViewport(): "mobile" | "tablet" | "desktop" {
  const w = window.innerWidth;
  if (w < 760) return "mobile";
  if (w < 1200) return "tablet";
  return "desktop";
}
